import { useState } from 'react';
import PropTypes from 'prop-types';

import SDK from '../../../Components/SDK';
import logger from '../../../Helpers/logger';
import {
    DEFAULT_COUNTRY_CODE,
    DEFAULT_DIAL_CODE,
    PHONE_MAX_LENGTH,
    sanitisePhone,
} from '../../../Helpers/formValidation';
import { isAppOnline } from '../../../Helpers/Utility';
import { T, TOAST } from '../../../localization/toastKeys';
import { useLanguage } from '../../../localization/useLanguage';
import { Dialog } from '../../../ui/Dialog';
import DialogFrame from '../../../ui/DialogFrame';
import DialogHead from '../../../ui/DialogFrame/DialogHead';
import DialogToaster from '../../../ui/DialogFrame/DialogToaster';
import { showDialogError, showOfflineError } from '../../../ui/DialogFrame/dialogToast';
import { cn } from '../../../ui/lib/utils';
import CountryPicker from '../../../ui/CountryPicker';
import { CONTACT_ADD_ERROR, USERNAME_MAX_LENGTH } from '../constants';
import dialogStyles from '../../../Components/WebChat/Common/ContactList/dialog.module.scss';
import styles from './index.module.scss';

const INITIAL_COUNTRY = { countryCode: DEFAULT_COUNTRY_CODE, dialCode: DEFAULT_DIAL_CODE };

const ERROR_MESSAGE_KEYS = {
    [CONTACT_ADD_ERROR.PHONE_IN_CONTACTS]: 'createChat.ERR_PHONE_IN_CONTACTS',
    [CONTACT_ADD_ERROR.USER_NOT_FOUND]: 'createChat.ERR_USER_NOT_FOUND',
    [CONTACT_ADD_ERROR.CANNOT_ADD_YOURSELF]: 'createChat.ERR_CANNOT_ADD_YOURSELF',
};

const FormField = ({ id, label, ctrlClassName, children }) => (
    <div className={dialogStyles.field}>
        <label className={dialogStyles.label} htmlFor={id}>
            {label}
        </label>
        <div className={cn(dialogStyles.ctrl, ctrlClassName)}>{children}</div>
    </div>
);

FormField.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    ctrlClassName: PropTypes.string,
    children: PropTypes.node.isRequired,
};

/**
 * New Contact pop-up (FR60 screen 60.2): saves a DazWay user found by username, phone number, or both.
 * The host remounts it on every opening, so each one starts with an empty form.
 *
 * @param {boolean} open Whether the pop-up is shown.
 * @param {Function} onClose Returns to the pop-up it was opened from, saving nothing.
 * @param {Function} onSaved Called with the saved contact.
 */
const NewContactDialog = ({ open, onClose, onSaved }) => {
    const { t } = useLanguage();
    const [username, setUsername] = useState('');
    const [country, setCountry] = useState(INITIAL_COUNTRY);
    const [phone, setPhone] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const hasLookupValue = username.trim() !== '' || phone.trim() !== '';

    const handleSave = async () => {
        if (!hasLookupValue || isSubmitting) return;
        if (!isAppOnline()) {
            showOfflineError();
            return;
        }
        setIsSubmitting(true);
        const response = await SDK.addContact(username.trim(), country.dialCode, phone.trim());
        setIsSubmitting(false);

        if (response?.statusCode === 200) {
            onSaved(response.data);
            return;
        }
        const messageKey = ERROR_MESSAGE_KEYS[response?.errorCode];
        if (messageKey) {
            showDialogError(t(messageKey));
            return;
        }
        logger.error('Add contact failed', { statusCode: response?.statusCode });
        showDialogError(t(`${TOAST}.${T.ACTION_FAILED_RETRY}`, { action: t('createChat.ACTION_CONTACT_SAVING') }));
    };

    return (
        <Dialog open={open} onOpenChange={isOpen => !isOpen && open && onClose()}>
            <DialogFrame>
                <DialogToaster open={open} />
                <DialogHead title={t('createChat.NEW_CONTACT_TITLE')} onClose={onClose} />

                <FormField id="create-chat-username" label={t('createChat.USERNAME')}>
                    <span className={styles.prefix}>@</span>
                    <input
                        id="create-chat-username"
                        autoComplete="off"
                        value={username}
                        maxLength={USERNAME_MAX_LENGTH}
                        placeholder={t('createChat.USERNAME_PLACEHOLDER')}
                        onChange={event => setUsername(event.target.value)}
                    />
                </FormField>

                <FormField id="create-chat-phone" label={t('createChat.PHONE_NUMBER')} ctrlClassName={styles.phoneCtrl}>
                    <CountryPicker
                        countryCode={country.countryCode}
                        dialCode={country.dialCode}
                        onSelect={setCountry}
                    />
                    <span className={styles.divider} />
                    <input
                        id="create-chat-phone"
                        type="tel"
                        inputMode="numeric"
                        autoComplete="off"
                        value={phone}
                        maxLength={PHONE_MAX_LENGTH}
                        placeholder={t('createChat.PHONE_PLACEHOLDER')}
                        onChange={event => setPhone(sanitisePhone(event.target.value))}
                    />
                </FormField>

                <div className={dialogStyles.actions}>
                    <button
                        type="button"
                        className={cn(dialogStyles.button, dialogStyles.primary)}
                        disabled={!hasLookupValue || isSubmitting}
                        onClick={handleSave}
                    >
                        {t('createChat.SAVE')}
                    </button>
                </div>
            </DialogFrame>
        </Dialog>
    );
};

NewContactDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired,
};

export default NewContactDialog;
